import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {getChannelNameFromChannel} from 'nutshell-core/channels/helpers';

import type {ChannelType} from '../../channels/types';
import {Link} from '../../link/link';
import {SelectPickerSearch} from '../../select-picker-search/select-picker-search';

import {SourcePickerOptionLabel} from './source-picker-option-label';
import type {Source} from './types';

import styles from './sources-select.module.css';

type Props = {
    channel?: ChannelType;
    sourceOptions: Source[];
    onSelectSource: (arg1: string) => void;
    canAccessSetup: boolean;
    onBlur?: () => void;
    minHeight?: number;
    preventScrollOnMenuOpen?: boolean;
    menuZIndex?: number;
};

export function SourcesSelect(props: Props) {
    const {
        channel: channelType,
        canAccessSetup,
        minHeight = 34,
        preventScrollOnMenuOpen = true,
    } = props;

    const getPlaceholderText = (): string | React.ReactNode => {
        return channelType ? (
            <>
                Search <strong>{getChannelNameFromChannel(channelType)}</strong> sources…
            </>
        ) : (
            'Search sources…'
        );
    };

    const getNoOptionsMessage = (): React.ReactElement => {
        let message;

        if (props.sourceOptions.length) {
            // No sources found via search
            message = (
                <>
                    No{' '}
                    {channelType ? (
                        <strong>{getChannelNameFromChannel(channelType)} </strong>
                    ) : undefined}
                    sources found
                </>
            );
        } else {
            // No sources exist for a specific channel, or at all
            message = (
                <>
                    {channelType ? (
                        <strong>{getChannelNameFromChannel(channelType)}</strong>
                    ) : (
                        'This channel'
                    )}{' '}
                    doesn‘t have any sources yet
                </>
            );
        }

        return (
            <div>
                <div className={styles['no-options-message-text']}>{message}</div>
                {canAccessSetup ? (
                    // @ts-expect-error
                    <Link as={RoutedLink} to='/setup/source' size='small' variant='primary'>
                        Configure sources in settings »
                    </Link>
                ) : (
                    <i>Admins can add new sources in settings</i>
                )}
            </div>
        );
    };

    return (
        <SelectPickerSearch
            minHeight={minHeight}
            placeholder={getPlaceholderText()}
            noOptionsMessage={getNoOptionsMessage()}
            autoFocus={true}
            openMenuOnFocus={true}
            preventScrollOnMenuOpen={preventScrollOnMenuOpen}
            onBlur={props.onBlur}
            options={props.sourceOptions.map((opt) => {
                return {value: opt.id, label: opt.name, type: opt.type, channel: opt.channel};
            })}
            onChange={(source: any) => {
                if (source) {
                    props.onSelectSource(source.value);
                }
            }}
            formatOptionLabel={SourcePickerOptionLabel}
            menuZIndex={props.menuZIndex}
        />
    );
}
